<script setup>
import { ref, onMounted, watch, computed, nextTick } from "vue";
import { DataTable } from "simple-datatables";
import "simple-datatables/dist/style.css";
import UserDetail from "./UserDetail.vue";
import axios from "axios";
import { BACK_END_URL } from "@/utilities/config";

const provinciaFilter = ref(sessionStorage.getItem("provincia") || "");
const cantonFilter = ref(sessionStorage.getItem("canton") || "");
const distritoFilter = ref(sessionStorage.getItem("distrito") || "");
const cedula = ref("");
const suggestions = ref([]);
const showDropdown = ref(false);
const totalUsers = ref(0); // Almacenar el número total de usuarios
const newLimit = ref(1000);
const isLoading = ref(false); // Estado de carga

const tempShowCensado = ref(false);
const tempShowVerificado = ref(false);

const tableRef = ref(null);
const selectedUser = ref(null);
const showModal = ref(false);
let dataTableInstance = null;
let usersData = [];

const headers = computed(() => {
  return props.provinceId === "8"
    ? { provincia: "Consulado", canton: "País", distrito: "Ciudad" }
    : { provincia: "Provincia", canton: "Cantón", distrito: "Distrito" };
});

const filters = ref({
  showCensado: false,
  showVerificado: false,
  province: "",
  canton: "",
  distrito: "",
});

const props = defineProps({
  provinceId: String, // El ID de la provincia recibido del padre
  applyFilters: {
    type: Boolean,
    default: false,
  },
  isColaborador: {
    type: Boolean,
  },
});

const isDesktop = ref(false);

// Función para detectar el tamaño de la pantalla
const checkScreenSize = () => {
  isDesktop.value = window.matchMedia("(min-width: 768px)").matches;
};

// Filtrar los usuarios basados en distelec.codelec

const reloadTableWithLimit = async (limite) => {
  if (limite <= 0) {
    alert("El límite debe ser un número mayor que 0.");
    return;
  }
  tempShowVerificado.value = false;
  tempShowCensado.value = false;
  sessionStorage.removeItem(`cachedUsers_${props.provinceId}`);
  const usersTableData = await fetchUsers(1, limite);
  if (usersTableData.length > 0) {
    initializeTable(usersTableData);
  }
};

const fetchUsers = async (pagina = 1, limite = 1000) => {

  isLoading.value = true; // Activar spinner
  const token = sessionStorage.getItem("token");

  try {
    let data;
    let users;

    // Construir query params solo si se aplican filtros
    let queryParams = `page=${pagina}&limit=${limite}`;
    if (filters.value.showCensado) queryParams += `&censado=true`;
    if (filters.value.showVerificado) queryParams += `&verificado=true`;

    // Si NO es colaborador, agregar el filtro de provincia
    if (!props.isColaborador && props.provinceId) {
      queryParams += `&provincia=${props.provinceId}`;
    }

    // Determinar el endpoint según isColaborador
    const endpoint = props.isColaborador
      ? `${BACK_END_URL}/analytics/voters/junta`
      : `${BACK_END_URL}/analytics/voters`;

    // Hacer la petición al backend
    const response = await axios.get(`${endpoint}?${queryParams}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    data = response.data;
    users = response.data.data;

    totalUsers.value = response.data.total;

    if (!users || users.length === 0) {
      console.warn("No hay datos con los filtros aplicados.");
      usersData = [];
      initializeTable([]);
      return [];
    }
    usersData = users;

    // Formatear datos para la tabla
    return users.map(formatUserData);
  } catch (error) {
    console.error("Error fetching data:", error);
    return [];
  } finally {
    isLoading.value = false; // Desactivar spinner
  }
};

// Función para formatear datos antes de pasarlos a la tabla
const formatUserData = (user) => {
  const {
    cedula,
    nombre,
    primer_apellido,
    segundo_apellido,
    distelec,
    codelec,
    junta,
    centro_educ,
    num_elector,
    direccion_exacta,
    telefono,
    email,
    fechanac,
    edad,
    fechacaduc,
    movimiento,
    estado,
    sector_pln,
    militante_otro_partido,
    convencion_2017,
    convencion_2021,
    convencion_2025,
    ma_distrital,
    ma_cantonal,
    ma_provincial,
    ma_nacional,
    directorio,
    colaboracion,
    pvh,
    censado,
    verificado,
    comentario,
  } = user;

  const formatBoolean = (value) => `
    <span class="badge ${value ? "badge-success" : "badge-secondary"}">
      <i class="${value ? "fas fa-check-circle" : "fas fa-times-circle"}"></i>
      ${value ? "Sí" : "No"}
    </span>
  `;
  const formatInt = (int) => {
    if (int === null || int === undefined) return '-';
    if (int === 0) return '-';

    const parsedInt = parseInt(int);
    return isNaN(parsedInt) ? '-' : parsedInt.toString();
  };

  const capitalizeFirst = (str) => {
    if (str == 'N/A') return '-';

    if (!str || typeof str !== 'string') return '-';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const formatDate = (date) => {
    if (!date) return "";
    const year = date.slice(0, 4);
    const month = date.slice(4, 6);
    const day = date.slice(6, 8);
    return `${day}-${month}-${year}`;
  };

  return [
    cedula.toString(),
    nombre || '-',
    primer_apellido || '-',
    segundo_apellido || '-',
    distelec?.provincia || "-",
    distelec?.canton || "-",
    distelec?.distrito || "-",
    formatInt(codelec),
    junta || '-',
    centro_educ || '-',
    num_elector || '-',
    direccion_exacta || '-',
    formatInt(telefono),
    email || '-',
    formatInt(edad),
    formatDate(fechanac),
    formatDate(fechacaduc),
    capitalizeFirst(movimiento),
    capitalizeFirst(estado),
    sector_pln || '-',
    capitalizeFirst(colaboracion),
    formatBoolean(militante_otro_partido),
    formatBoolean(convencion_2017),
    formatBoolean(convencion_2021),
    formatBoolean(convencion_2025),
    formatBoolean(ma_distrital),
    formatBoolean(ma_cantonal),
    formatBoolean(ma_provincial),
    formatBoolean(ma_nacional),
    formatBoolean(directorio),
    formatBoolean(pvh),
    formatBoolean(censado),
    formatBoolean(verificado),
    comentario || '-'
  ];
};




const fetchSuggestions = async () => {
  if (cedula.value.length < 5) {
    suggestions.value = [];
    showDropdown.value = false;
    return;
  }

  try {
    const token = sessionStorage.getItem("token");

    // Determinar el endpoint basado en isColaborador
    const endpoint = props.isColaborador
      ? `${BACK_END_URL}/analytics/junta/search-by-id`
      : `${BACK_END_URL}/analytics/search-by-id`;

    const response = await axios.get(`${endpoint}/${cedula.value}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    suggestions.value = response.data ? [response.data] : [];
    showDropdown.value = suggestions.value.length > 0;
  } catch (error) {
    console.error("Error fetching suggestions:", error);
    suggestions.value = [];
    showDropdown.value = false;
  }
};


const openModal = (userOrCedula) => {
  // Si se proporciona un objeto de usuario completo (clic en el dropdown)
  selectedUser.value = userOrCedula;
  showModal.value = true;
};

const openModalRow = (cedula) => {
  selectedUser.value = usersData.find(
    (user) => user.cedula.toString() === cedula.toString()
  ); // Asegurarse de que ambos sean cadenas
  showModal.value = true;
};

// Reinitialize the table every time the data changes
const initializeTable = (usersTableData) => {
  // Verificar si tableRef no es nulo antes de intentar destruir la instancia anterior de DataTable
  if (tableRef.value && dataTableInstance) {
    dataTableInstance.destroy();
  }

  // Verificar si tableRef está disponible antes de destruir la instancia anterior
  if (tableRef.value) {
    // Crear un nuevo DataTable con los datos actualizados
    dataTableInstance = new DataTable(tableRef.value, {
      searchable: true,
      fixedHeight: true,
      labels: {
        placeholder: "Buscar...", // Placeholder de la barra de búsqueda
        perPage: "Mostrar {select} registros por página",
        noRows: "No se encontraron registros",
        info: "Mostrando {start} a {end} de {rows} registros",
        loading: "Cargando...",
        noResults: "No se encontraron coincidencias",
        previous: "Anterior",
        next: "Siguiente",
      },
    });

    dataTableInstance.rows.add(usersTableData);

    // Verificar si el cuerpo de la tabla existe
    const tableBody = tableRef.value.querySelector("tbody");
    if (!tableBody) {
      console.error("Cuerpo de la tabla (tbody) no encontrado.");
      return;
    }

    // Agregar event listener para clics en las filas
    tableBody.addEventListener("click", (event) => {
      const row = event.target.closest("tr");
      if (row) {
        const userId = row.children[0]?.textContent;
        if (userId) {
          openModalRow(userId); // Abrir modal para la fila seleccionada
        } else {
          console.warn("ID de usuario no encontrado en la fila.");
        }
      }
    });

  } else {
    console.error("tableRef.value es nulo. No se puede inicializar DataTable.");
  }
};


// Watch for prop changes and update chart
watch(
  () => props.provinceId,
  async () => {
    const usersTableData = await fetchUsers(); // Volver a obtener datos basados en el provinceId actualizado
    
    if (usersTableData.length > 0) {
      initializeTable(usersTableData); // Re-inicializar la tabla con los nuevos datos
    }
    
  },
  { immediate: true }
);

watch(
  filters,
  async () => {
    const usersTableData = await fetchUsers();
    if (usersTableData.length > 0) {
      initializeTable(usersTableData);
    }
  },
  { deep: true }
);

watch([provinciaFilter, cantonFilter, distritoFilter], async () => {
  const usersTableData = await fetchUsers();
  if (usersTableData.length > 0) {
    initializeTable(usersTableData);
  }
});

onMounted(() => {
  checkScreenSize();
});

const applyFilters = () => {
  filters.value.showCensado = tempShowCensado.value;
  filters.value.showVerificado = tempShowVerificado.value;
};

</script>

<template>
  <div class="card">
    <div class="p-3 pb-0 card-header">
      <div class="d-flex justify-content-between">
        <h6 v-if="!props.isColaborador" class="mb-0">Electores</h6>
        <h6 v-else class="mb-0">Electores en tu Centro de Votación</h6>
        <div class="d-flex d-sm-flex gap-1">
          <!-- Censado Checkbox -->
          <div class="form-check d-flex align-items-center">
            <input v-model="tempShowCensado" class="custom-checkbox" type="checkbox" id="censadoCheckbox"
              :disabled="isLoading" />
            <label class="form-check-label mb-0" for="censadoCheckbox"> Censado </label>
          </div>

          <!-- Verificado Checkbox -->
          <div class="form-check d-flex align-items-center">
            <input v-model="tempShowVerificado" class="custom-checkbox" type="checkbox" id="verificadoCheckbox"
              :disabled="isLoading" />
            <label class="form-check-label mb-0" for="verificadoCheckbox"> Verificado </label>
          </div>

          <button v-if="isDesktop" class="btn btn-dark btn-xs p-1 px-2 ms-3 mb-1" @click="applyFilters"
            :disabled="isLoading">
            <i class="fas fa-filter me-2"></i>
            {{ isLoading ? "Aplicando..." : "Aplicar Filtros" }}
          </button>

        </div>
      </div>
      <div v-if="!isDesktop" class="d-flex gap-1 justify-content-end my-3">

        <!-- Apply Filters Button -->
        <button class="btn btn-dark btn-xs p-1 px-2 ms-2 mb-0" @click="applyFilters" :disabled="isLoading">
          <i class="fas fa-filter me-2"></i>
          {{ isLoading ? "Aplicando..." : "Aplicar Filtros" }}
        </button>
      </div>
      <hr class="horizontal dark mt-0 mb-1" />

      <div class="row pe-3 list-row align-items-right mt-4">
        <!-- Columna Izquierda: Provincia, Cantón, Distrito -->
        <div v-if="!isLoading" class="col-lg-6 col-12 d-flex align-items-center justify-content-end">
          <div class="form-check form-check-inline w-100 ps-0">
            <input v-model="cedula" class="form-control text-md w-100 large"
              :style="errorMessage2 ? 'border-bottom: 3px solid #f5365c;' : ''" placeholder="# Cédula (ej. 101230507)"
              type="tel" @input="fetchSuggestions" />
            <ul v-if="showDropdown" class="dropdown-menu show w-40">
              <li v-for="user in suggestions" :key="user.cedula" @click="openModal(user)">
                <a v-if="user.cedula" class="dropdown-item">{{ user.cedula }} - {{ user.nombre }}
                  {{ user.primer_apellido }}
                  {{ user.segundo_apellido }}</a>
                <a v-else class="dropdown-item">No hay coincidencias</a>
              </li>
            </ul>
          </div>
        </div>

        <div v-if="!isLoading" class="col-lg-6 col-12 d-flex align-items-center justify-content-end mt-4 mt-sm-0">
          <span>Mostrando</span>
          <input type="number" id="limitInput" class="form-control w-20 mx-2" v-model="newLimit" min="1"
            placeholder="Escribe el límite" />
          <span>votantes de {{ totalUsers.toLocaleString() }}</span>
          <button class="btn btn-dark btn-xs m-0 ms-2" @click="reloadTableWithLimit(newLimit)" :disabled="isLoading">
            <span><i class="fas fa-sync-alt"></i></span>
          </button>
        </div>
      </div>
    </div>

    <div class="d-flex justify-content-center mt-5" v-if="isLoading">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>

    <div v-else class="table-responsive">
      <table v-if="usersData.length > 0" ref="tableRef" class="table">
        <thead class="thead-light">
          <tr>
            <th>Cédula</th>
            <th>Nombre</th>
            <th>Primer Apellido</th>
            <th>Segundo Apellido</th>
            <th>{{ headers.provincia }}</th>
            <th>{{ headers.canton }}</th>
            <th>{{ headers.distrito }}</th>
            <th>Codelec</th>
            <th>Junta</th>
            <th>Centro Educativo</th>
            <th># Elector</th>
            <th>Dirección</th>
            <th>Teléfono</th>
            <th>Email</th>
            <th>Edad</th>
            <th>Fecha de Nacimiento</th>
            <th>Fecha Caducidad</th>
            <th>Movimiento</th>
            <th>Estado</th>
            <th>Sector PLN</th>
            <th>Colaboración</th>
            <th>Militante otro partido</th>
            <th>Convención 2017</th>
            <th>Convención 2021</th>
            <th>Convención 2025</th>
            <th>MA Cantonal</th>
            <th>MA Distrital</th>
            <th>MA Provincial</th>
            <th>MA Nacional</th>
            <th>Directorio</th>
            <th>PVH</th>
            <th>Censado</th>
            <th>Verificado</th>
          </tr>
        </thead>
        <tbody></tbody>
      </table>
      <div v-else class="no-data-message">No hay datos</div>

    </div>
    <UserDetail v-if="showModal" :user="selectedUser" @close="showModal = false" />
  </div>

</template>

<style>
tbody tr:hover {
  background-color: #e9ecef;
  cursor: pointer;
}

.table th,
.table td {
  text-align: start !important;
}

.dataTable-wrapper .dataTable-top {
  padding-top: 0.3rem !important;
  padding-bottom: 0.7rem !important;
}

.card {
  min-height: 300px;
}

.dropdown-menu {
  z-index: 998 !important;
}

.no-data-message {
  text-align: center;
  font-size: 16px;
  color: #888;
  padding: 20px;

  /* Centering the message */
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  /* Ensure the message takes up the full height of the container */
}


.custom-checkbox {
  border-radius: 1rem;
  width: 18px;
  height: 18px;
  accent-color: #198754;
  cursor: pointer;
}
</style>
